import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { SEO, FAQStructuredData } from '@/components/SEO';
import { RelatedPages } from '@/components/RelatedPages';

const PRICING_DATA = [
  {
    category: 'Modular Office Workstations',
    range: '₹5,320 – ₹2,00,000+',
    items: [
      { name: 'Linear Workstation (2-Seater)', range: '₹5,320 – ₹18,000' },
      { name: 'Cluster of 4 Workstation', range: '₹22,000 – ₹55,000' },
      { name: 'L-Shaped Executive Desk', range: '₹15,000 – ₹45,000' },
      { name: 'Manager Cabin Setup', range: '₹35,000 – ₹1,20,000' },
    ],
    factors: ['Configuration (linear vs L-shaped)', 'Number of seats', 'Material grade (standard vs premium laminate)', 'Cable management options', 'Accessory add-ons (screen dividers, CPU holders)'],
  },
  {
    category: 'Privacy Booths & Phone Pods',
    range: '₹1,35,000 – ₹5,00,000+',
    items: [
      { name: '1-Person Phone Pod', range: '₹1,35,000 – ₹2,20,000' },
      { name: '2-Person Meeting Pod', range: '₹2,00,000 – ₹3,00,000' },
      { name: '4-Person Conference Booth', range: '₹2,80,000 – ₹4,20,000' },
      { name: '6-8 Person Large Pod', range: '₹3,50,000 – ₹5,00,000+' },
    ],
    factors: ['Size (1-person to 8-person)', 'Acoustic rating (dB reduction level)', 'Interior features (LED, ventilation, USB)', 'Glass type (single vs double-pane)', 'Customization (branding, color)'],
  },
  {
    category: 'Storage Cabinets & Solutions',
    range: '₹4,226 – ₹50,000+',
    items: [
      { name: 'Under-Desk Pedestal', range: '₹4,226 – ₹8,000' },
      { name: 'Sliding Door Cabinet', range: '₹8,000 – ₹18,000' },
      { name: 'Filing Cabinet (4-Drawer)', range: '₹12,000 – ₹22,000' },
      { name: 'Full-Height Storage Unit', range: '₹18,000 – ₹50,000' },
    ],
    factors: ['Material (metal vs laminate)', 'Lock type (key vs combination)', 'Number of shelves/drawers', 'Dimensions (height, width)', 'Fire rating (if applicable)'],
  },
];

const FAQS = [
  { question: 'What is the starting price for modular office workstations?', answer: 'Modular office workstations at MFS start from ₹5,320 for a basic 2-seater linear configuration. Prices vary based on material grade, size, and accessories like cable management trays and screen dividers.' },
  { question: 'Why is modular furniture more cost-effective than traditional carpentry?', answer: 'Modular furniture eliminates on-site labor costs, reduces installation time from weeks to hours, and uses factory-precision manufacturing that minimizes material waste. Over 5 years, modular furniture typically costs 30-40% less than traditional carpentry when factoring in maintenance and reconfiguration costs.' },
  { question: 'Is delivery and installation included in the price?', answer: 'Free delivery and professional installation are included for orders within Mumbai and Navi Mumbai. For other cities, shipping charges are calculated based on distance. All products use our tool-free Kholo-Jodo-Lagalo assembly system.' },
  { question: 'Do you offer bulk pricing for corporate orders?', answer: 'Yes, we offer significant volume discounts for bulk orders. Contact us at +91-9594685818 or fill our bulk inquiry form for a customized quotation. Orders above 50 units typically receive 15-25% off listed prices.' },
  { question: 'How does the pricing compare to IKEA or local carpenters?', answer: 'MFS offers factory-direct pricing that is typically 20-30% lower than IKEA for comparable quality. Compared to local carpenters, our modular solution offers superior finish quality, warranty coverage, and the ability to disassemble and relocate — something traditional carpentry cannot match.' },
];

export default function PricingGuide() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO
        title="Modular Furniture Price Guide 2025 | MFS India"
        description="Complete pricing guide for modular office furniture in India. Workstations from ₹5,320, privacy booths from ₹1,35,000, storage from ₹4,226. Factory-direct pricing."
        canonical="/pricing-guide"
        keywords="modular furniture price, office workstation cost, privacy booth price india, modular office furniture price list"
      />
      <FAQStructuredData faqs={FAQS} />
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-16 max-w-5xl">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-2">
          Modular Office Furniture — Price Guide 2025
        </h1>
        <p className="text-gray-500 text-sm mb-12 max-w-2xl">
          Transparent, factory-direct pricing from Ezeefit Modular Furniture Pvt. Ltd. All prices are exclusive of GST. Custom configurations available on request.
        </p>

        {/* Pricing Tables */}
        {PRICING_DATA.map((cat, idx) => (
          <section key={idx} className="mb-14">
            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-[#c5a059] text-sm font-bold tracking-[0.2em]">0{idx + 1}</span>
              <div className="h-px bg-[#c5a059] flex-1 mt-1" />
            </div>
            <h2 className="text-xl font-extrabold text-[#1a1a1a] mb-1">{cat.category}</h2>
            <p className="text-[#c5a059] text-sm font-bold mb-6">Price Range: {cat.range}</p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-left text-sm border border-gray-100">
                <thead>
                  <tr className="bg-[#F8F6F1]">
                    <th className="px-4 py-3 font-bold text-[#1a1a1a] text-xs uppercase tracking-wider">Product</th>
                    <th className="px-4 py-3 font-bold text-[#1a1a1a] text-xs uppercase tracking-wider text-right">Price Range</th>
                  </tr>
                </thead>
                <tbody>
                  {cat.items.map((item, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-700">{item.name}</td>
                      <td className="px-4 py-3 text-[#1a1a1a] font-semibold text-right">{item.range}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Price depends on:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {cat.factors.map((f, i) => (
                  <li key={i} className="text-xs text-gray-500 flex items-start gap-2">
                    <span className="text-[#c5a059] mt-0.5">•</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}

        {/* CTA */}
        <div className="bg-[#1a1a1a] p-8 sm:p-10 text-center mb-14">
          <p className="text-white text-lg font-bold mb-2">Need a Custom Quote?</p>
          <p className="text-gray-400 text-sm mb-6">Get factory-direct pricing for your specific requirements. Bulk discounts available.</p>
          <Link to="/bulk-inquiry">
            <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-white rounded-none px-8 py-4 text-xs tracking-[0.15em] font-bold">
              REQUEST CUSTOM QUOTE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* FAQ Section */}
        <section>
          <h2 className="text-xl font-extrabold text-[#1a1a1a] mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#c5a059]" /> Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <details key={i} className="group border border-gray-100 open:border-[#c5a059] transition-colors">
                <summary className="flex justify-between items-center cursor-pointer px-5 py-4 text-sm font-semibold text-[#1a1a1a] hover:text-[#c5a059]">
                  {faq.question}
                  <span className="text-[#c5a059] group-open:rotate-45 transition-transform text-lg">+</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Interlinks */}
        <RelatedPages pageKey="pricingGuide" className="mt-12" />
      </div>

      <PremiumFooter />
    </div>
  );
}
